/**
 * src/marketing/legal.ts - source of truth for the getabeam.com LEGAL CENTER copy.
 *
 * Holds the three published policies (Terms of Service, Privacy Policy, Refund & Return
 * Policy) as structured content plus the shared legal metadata (entity, governing law,
 * effective date, contact channel). The page components in app/(marketing)/legal/* render
 * this; keep all wording here so the pages stay structural.
 *
 * Editing notes:
 *  - LEGAL_META is the single place to change the entity name, governing-law state, the
 *    contact channel, or the effective date. Update LEGAL_META.updated whenever you change
 *    any policy wording.
 *  - House style (and a hard product rule): NO em-dashes or en-dashes in any rendered copy.
 *  - This is standard template language. Have qualified counsel review it before relying on it.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Plain data module, web-only surface.
 */

export const LEGAL_META = {
  /** Contracting entity named throughout the policies. */
  entity: 'CompVer LLC',
  /** Long form used in policy openings, ties the marketing brand to the legal entity. */
  entityLong: 'CompVer LLC, doing business as CompVer Consulting',
  product: 'Beam',
  domain: 'getabeam.com',
  appUrl: 'https://getabeam.com',
  /** US state whose law governs the Terms and is the venue for disputes. */
  governingState: 'Oregon',
  /** Contact channel for legal, privacy, and support inquiries. */
  contactUrl: 'mailto:info@compver.com',
  contactDisplay: 'info@compver.com',
  /** "Last updated" date shown on every policy. Bump when wording changes. */
  updated: 'June 19, 2026',
} as const;

/** One paragraph = one string. Bullets render as a list under the paragraphs. */
export type LegalSection = {
  heading: string;
  body?: string[];
  bullets?: string[];
};

export type LegalDoc = {
  /** Route slug under /legal (e.g. "terms" -> /legal/terms). */
  slug: 'terms' | 'privacy' | 'refunds' | 'delete-account';
  title: string;
  /** Short description shown on the Legal Center hub. */
  summary: string;
  /** Lede paragraphs shown above the numbered sections. */
  intro: string[];
  sections: LegalSection[];
};

const M = LEGAL_META;

// ---------------------------------------------------------------------------
// Terms of Service
// ---------------------------------------------------------------------------

export const TERMS: LegalDoc = {
  slug: 'terms',
  title: 'Terms of Service',
  summary: 'The agreement that governs your use of Beam, including subscriptions, acceptable use, and liability.',
  intro: [
    `These Terms of Service (the "Terms") are a binding agreement between you and ${M.entityLong} ("${M.entity}", "we", "us", or "our"), and they govern your access to and use of the ${M.product} applications, websites, and services (together, the "Service").`,
    `By creating an account, subscribing, or otherwise using the Service, you agree to these Terms. If you are using the Service on behalf of a company or other organization, you represent that you are authorized to bind that organization, and "you" refers to that organization. If you do not agree to these Terms, do not use the Service.`,
  ],
  sections: [
    {
      heading: '1. The Service',
      body: [
        `${M.product} is software for trades and contractors that helps you capture leads, build estimates, schedule jobs, prepare permit and government filings, send invoices, accept payments, and run reporting. We may add, change, or remove features over time.`,
        `The Service is provided as a hosted subscription. It is not a substitute for professional, legal, accounting, tax, or licensing advice, and you remain solely responsible for the work you perform and the filings you submit.`,
      ],
    },
    {
      heading: '2. Eligibility and accounts',
      body: [
        'You must be at least 18 years old and able to form a binding contract to use the Service. You agree to provide accurate, current, and complete information when you register and to keep it up to date.',
        'You are responsible for safeguarding your account credentials and for all activity that occurs under your account, including activity by the users you invite. Notify us promptly if you believe your account has been accessed without authorization.',
      ],
    },
    {
      heading: '3. Your users',
      body: [
        'Your subscription may permit a set number of users. You are responsible for your users\' compliance with these Terms, for the accuracy of the data they enter, and for managing their access. Removing a user does not delete the data they created.',
      ],
    },
    {
      heading: '4. Subscriptions, billing, and payment',
      body: [
        `The Service is offered on a recurring subscription. By subscribing, you authorize us and our payment processor to charge the plan fee (currently $49 per month for the Crew plan, or as otherwise quoted) to your payment method on a recurring basis until you cancel.`,
        'Subscriptions renew automatically at the end of each billing period at the then-current rate unless cancelled before the renewal date. Fees are stated exclusive of taxes; you are responsible for any applicable sales, use, or similar taxes.',
        'Payments are processed by Stripe, Inc. We do not store full payment card numbers. Your use of payment features is also subject to Stripe\'s terms and privacy policy. If a charge fails, we may retry it and may suspend or limit access until payment is current.',
        'We may change our prices or plans. We will give you reasonable advance notice of a price change that affects your subscription, and the change will take effect on your next renewal. Continuing to use the Service after a price change takes effect constitutes acceptance of the new price.',
      ],
    },
    {
      heading: '5. Cancellation, suspension, and termination',
      body: [
        'You may cancel your subscription at any time from the billing portal in your account. Cancellation stops future renewals. Your access continues through the end of the billing period you have already paid for, after which it ends.',
        'Fees already paid are non-refundable except as described in our Refund and Return Policy or as required by law. Please review that policy, which is incorporated into these Terms.',
        'We may suspend or terminate your access if you breach these Terms, if your payment is overdue, if required by law, or to protect the Service or other users. If your subscription lapses or is cancelled, your access to the Service may be locked, although you will retain the ability to manage billing and reactivate where applicable.',
        'You may request an export of your data before your account is closed. After termination, we may delete your data in accordance with our Privacy Policy and our retention practices.',
      ],
    },
    {
      heading: '6. Your data and content',
      body: [
        'As between you and us, you own the business data and content you submit to the Service, including your customer records, estimates, invoices, photos, and documents ("Customer Data"). You grant us a worldwide, non-exclusive license to host, process, transmit, display, and back up Customer Data solely to provide, secure, and improve the Service and as otherwise permitted by our Privacy Policy.',
        'You are responsible for the accuracy, quality, and legality of Customer Data and for having the rights and any necessary consents to provide it to us, including consent to contact your own customers by email or text where you use those features.',
      ],
    },
    {
      heading: '7. Acceptable use',
      body: ['You agree not to:'],
      bullets: [
        'use the Service in violation of any law or regulation, or to infringe the rights of others;',
        'upload malicious code or attempt to gain unauthorized access to the Service, other accounts, or our systems;',
        'interfere with or disrupt the integrity or performance of the Service;',
        'reverse engineer, resell, or sublicense the Service except as expressly permitted;',
        'send unlawful, deceptive, or unsolicited communications using the Service\'s messaging features; or',
        'use the Service to store or transmit content that is unlawful, harassing, or otherwise objectionable.',
      ],
    },
    {
      heading: '8. AI-assisted features',
      body: [
        'The Service uses artificial intelligence to help draft estimates, summarize messages, extract information from documents, and assist with similar tasks. AI output may be inaccurate or incomplete. You are responsible for reviewing, correcting, and approving any AI-assisted output before relying on or sending it.',
        'AI features are a drafting aid only and do not constitute professional, legal, accounting, or licensing advice. Final pricing, approvals, and submissions remain your responsibility.',
      ],
    },
    {
      heading: '9. Permits and government filings',
      body: [
        'The Service helps you prepare and track permit packets, notices of commencement, and related government filings using data you already maintain. It prepares documents for your review; it does not file, notarize, attest, or record on your behalf, and it does not bypass any verification step required by a government office.',
        'You are responsible for reviewing each filing for accuracy and completeness, for any required notarization or attestation, and for submitting and recording the filing with the appropriate authority. Nothing in the Service is legal advice.',
      ],
    },
    {
      heading: '10. Third-party services',
      body: [
        'The Service relies on third-party providers, including for payments, messaging, email, AI processing, hosting, and app distribution. Your use of features that depend on these providers may be subject to their terms, and we are not responsible for third-party services or content. Links to third-party sites are provided for convenience and do not imply endorsement.',
      ],
    },
    {
      heading: '11. Intellectual property',
      body: [
        `The Service, including its software, design, and the ${M.product} name and marks, is owned by ${M.entity} and its licensors and is protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable, revocable right to use the Service during your subscription, subject to these Terms. We reserve all rights not expressly granted.`,
        'If you send us feedback or suggestions, you grant us a perpetual, royalty-free license to use them without restriction or obligation to you.',
      ],
    },
    {
      heading: '12. Disclaimer of warranties',
      body: [
        'The Service is provided "as is" and "as available" without warranties of any kind, whether express, implied, or statutory, including any implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or secure, or that it will meet your requirements. Some jurisdictions do not allow the exclusion of certain warranties, so some of these exclusions may not apply to you.',
      ],
    },
    {
      heading: '13. Limitation of liability',
      body: [
        `To the maximum extent permitted by law, ${M.entity} and its suppliers will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or goodwill, arising out of or related to your use of or inability to use the Service, even if advised of the possibility of such damages.`,
        `To the maximum extent permitted by law, our total liability for all claims arising out of or related to the Service will not exceed the amount you paid us for the Service in the twelve months before the event giving rise to the claim. Some jurisdictions do not allow certain limitations, so some of these limitations may not apply to you.`,
      ],
    },
    {
      heading: '14. Indemnification',
      body: [
        `You agree to indemnify and hold harmless ${M.entity} and its officers, employees, and agents from any claims, damages, liabilities, and expenses (including reasonable legal fees) arising out of your Customer Data, your use of the Service, your violation of these Terms, or your violation of any law or the rights of a third party.`,
      ],
    },
    {
      heading: '15. Changes to the Service and these Terms',
      body: [
        'We may modify the Service and these Terms from time to time. If we make a material change to these Terms, we will provide notice by updating the "Last updated" date and, where appropriate, through the Service or by other reasonable means. Changes take effect when posted unless stated otherwise. Your continued use of the Service after changes take effect constitutes acceptance.',
      ],
    },
    {
      heading: '16. Governing law and disputes',
      body: [
        `These Terms are governed by the laws of the State of ${M.governingState}, without regard to its conflict-of-laws rules. You agree that the state and federal courts located in ${M.governingState} have exclusive jurisdiction over any dispute arising out of or relating to these Terms or the Service, and you consent to personal jurisdiction and venue there, except that either party may seek injunctive relief in any court of competent jurisdiction.`,
      ],
    },
    {
      heading: '17. General',
      body: [
        'These Terms, together with the Privacy Policy and the Refund and Return Policy, are the entire agreement between you and us regarding the Service and supersede any prior agreements. If any provision is found unenforceable, the remaining provisions will remain in effect. Our failure to enforce a provision is not a waiver. You may not assign these Terms without our consent; we may assign them in connection with a merger, acquisition, or sale of assets. We are not liable for any delay or failure to perform caused by events beyond our reasonable control.',
      ],
    },
    {
      heading: '18. Contact',
      body: [
        `Questions about these Terms can be directed to ${M.entity} through ${M.contactDisplay}.`,
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Privacy Policy
// ---------------------------------------------------------------------------

export const PRIVACY: LegalDoc = {
  slug: 'privacy',
  title: 'Privacy Policy',
  summary: 'What information Beam collects, how we use and share it, and the choices and rights you have.',
  intro: [
    `This Privacy Policy explains how ${M.entityLong} ("${M.entity}", "we", "us", or "our") collects, uses, and shares information when you use the ${M.product} applications, websites, and services (the "Service").`,
    `It also describes the rights and choices you have. By using the Service, you agree to this Policy. If you do not agree, do not use the Service.`,
  ],
  sections: [
    {
      heading: '1. Information we collect',
      body: ['We collect the following categories of information:'],
      bullets: [
        'Account information: name, business name, email address, phone number, and the credentials you use to sign in.',
        'Business and customer data you enter: leads, customers, estimates, invoices, jobs, schedules, photos, documents, and the contact details of the people you do business with.',
        'Payment information: when you subscribe, our payment processor (Stripe) collects and processes your payment details. We receive limited information such as a payment confirmation and the last four digits of a card; we do not store full card numbers.',
        'Usage and device data: log data, device and browser type, IP address, app version, and how you interact with the Service, collected to operate and improve it.',
        'Communications: messages you send to us, and the content of email and text messages you send or receive through the Service\'s messaging features.',
      ],
    },
    {
      heading: '2. How we use information',
      body: ['We use information to:'],
      bullets: [
        'provide, maintain, secure, and improve the Service;',
        'process subscriptions, payments, and account administration;',
        'generate AI-assisted drafts and other features you request;',
        'communicate with you about your account, transactions, support, and service updates;',
        'detect, prevent, and address fraud, abuse, and security issues; and',
        'comply with legal obligations and enforce our terms.',
      ],
    },
    {
      heading: '3. AI processing',
      body: [
        'Some features send content you provide, such as message text, document images, or notes, to our AI provider so that it can return a draft or extracted information. This processing is performed to deliver the feature you requested. We work to limit what is sent to what is needed for the task.',
      ],
    },
    {
      heading: '4. How we share information',
      body: ['We do not sell your personal information. We share information only as follows:'],
      bullets: [
        'Service providers and subprocessors who help us operate the Service, including payment processing (Stripe), messaging and email delivery, AI processing, hosting, and analytics, under contracts that limit their use of the information.',
        'At your direction, such as when you send an estimate, invoice, or message to one of your customers through the Service.',
        'For legal reasons, such as to comply with applicable law, a lawful request, or legal process, or to protect the rights, safety, and property of us, our users, or others.',
        'In a business transfer, such as a merger, acquisition, or sale of assets, in which information may be transferred as part of the transaction.',
      ],
    },
    {
      heading: '5. Your end customers\' data',
      body: [
        'When you use the Service to manage information about your own customers, you control that information and are responsible for handling it lawfully, including providing any required notices and obtaining any required consents (for example, before sending marketing email or text messages). We process that information on your behalf to provide the Service.',
      ],
    },
    {
      heading: '6. Data retention',
      body: [
        'We retain information for as long as your account is active and as needed to provide the Service, comply with our legal obligations, resolve disputes, and enforce our agreements. When information is no longer needed, we take reasonable steps to delete or de-identify it. You can request an export of your data before closing your account.',
      ],
    },
    {
      heading: '7. Security',
      body: [
        'We use administrative, technical, and physical safeguards designed to protect information, including encryption in transit and access controls. No method of transmission or storage is completely secure, so we cannot guarantee absolute security. Please use a strong, unique password and keep your credentials confidential.',
      ],
    },
    {
      heading: '8. Your rights and choices',
      body: [
        'Depending on where you live, you may have rights to access, correct, delete, or obtain a copy of your personal information, and to object to or restrict certain processing. You can update much of your account information directly in the Service, and you can opt out of marketing email by following the unsubscribe instructions in those messages. To make a rights request, contact us using the details below. We will respond as required by applicable law and will not discriminate against you for exercising your rights.',
      ],
    },
    {
      heading: '9. Cookies and tracking',
      body: [
        'Our website and web app use cookies and similar technologies to keep you signed in, remember preferences, and understand usage. You can control cookies through your browser settings, although disabling them may affect how the Service works.',
      ],
    },
    {
      heading: '10. Data location and transfers',
      body: [
        'We and our service providers may process and store information in the United States and other countries. Where information is transferred across borders, we take steps to ensure it is handled consistently with this Policy and applicable law.',
      ],
    },
    {
      heading: '11. Children\'s privacy',
      body: [
        'The Service is intended for businesses and is not directed to children under 13, and we do not knowingly collect personal information from children. If you believe a child has provided us information, contact us and we will take appropriate steps to delete it.',
      ],
    },
    {
      heading: '12. Changes to this Policy',
      body: [
        'We may update this Policy from time to time. We will indicate the date of the latest revision in the "Last updated" line and, for material changes, provide additional notice where appropriate. Your continued use of the Service after a change takes effect constitutes acceptance.',
      ],
    },
    {
      heading: '13. Contact',
      body: [
        `For privacy questions or to exercise your rights, contact ${M.entity} through ${M.contactDisplay}.`,
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Refund and Return Policy
// ---------------------------------------------------------------------------

export const REFUNDS: LegalDoc = {
  slug: 'refunds',
  title: 'Refund and Return Policy',
  summary: 'How subscription billing, cancellation, and refunds work for Beam.',
  intro: [
    `This Refund and Return Policy applies to subscriptions to the ${M.product} service provided by ${M.entityLong} ("${M.entity}", "we", "us", or "our"). It is part of our Terms of Service.`,
  ],
  sections: [
    {
      heading: '1. Subscriptions and billing',
      body: [
        `${M.product} is a recurring subscription, billed in advance each billing period (currently $49 per month for the Crew plan, or as otherwise quoted). Your subscription renews automatically until you cancel.`,
      ],
    },
    {
      heading: '2. No refunds',
      body: [
        'Payments are non-refundable. We do not provide refunds or credits for partial billing periods, unused time, or periods during which your account was active but not used, except where required by law or as expressly stated in this Policy.',
      ],
    },
    {
      heading: '3. Cancel anytime',
      body: [
        'You can cancel your subscription at any time from the billing portal in your account. When you cancel, your subscription will not renew, and you will keep access through the end of the billing period you have already paid for. You will not be charged again after you cancel.',
      ],
    },
    {
      heading: '4. Digital service, no physical returns',
      body: [
        `${M.product} is a digital, hosted software service. There is no physical product to ship or return. The way to stop future charges is to cancel your subscription as described above.`,
      ],
    },
    {
      heading: '5. Failed payments and lapsed access',
      body: [
        'If a renewal payment fails, we may retry the charge and may suspend or lock access to the Service until payment is current. You can update your payment method or reactivate from the billing portal. Locking access for non-payment does not entitle you to a refund of amounts already paid.',
      ],
    },
    {
      heading: '6. Billing errors and exceptions',
      body: [
        'If you believe you were charged in error, such as a duplicate charge, contact us promptly and we will review it. We will refund charges where required by applicable law or where our review confirms a billing error. Any refund we issue is returned to the original payment method.',
      ],
    },
    {
      heading: '7. How to cancel or get help',
      body: [
        `To cancel, open the billing portal in your ${M.product} account. For questions about billing or this Policy, contact ${M.entity} through ${M.contactDisplay}.`,
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Account Deletion (public page for the Google Play Data Safety deletion URL)
// ---------------------------------------------------------------------------

export const DELETE_ACCOUNT: LegalDoc = {
  slug: 'delete-account',
  title: 'Account Deletion',
  summary: 'How to permanently delete your Beam account and data from inside the app, and how to request deletion if you no longer have the app.',
  intro: [
    `This page explains how to delete your ${M.product} account and the data associated with it. ${M.product} is provided by ${M.entityLong} ("${M.entity}", "we", "us", or "our").`,
    `Deleting your account is permanent. It removes your organization and all of its data and cannot be undone.`,
  ],
  sections: [
    {
      heading: '1. Delete your account from the app',
      body: [
        `You can delete your account at any time from inside the ${M.product} app. Open the app, go to the Account tab, and tap "Delete account". You will be asked to re-enter your password and to confirm before the deletion proceeds.`,
        'Account deletion is an owner action. If you are a team member rather than the account owner, ask your account owner to delete the organization, or to remove your user if you only want your own access ended.',
      ],
    },
    {
      heading: '2. What gets deleted',
      body: ['When you delete your account, we permanently remove your organization and the data it contains, including:'],
      bullets: [
        'your account and the user records for everyone in the organization;',
        'your customers, leads, estimates, invoices, jobs, and schedules;',
        'photos, documents, and files you uploaded;',
        'messages and communication history stored in the Service.',
      ],
    },
    {
      heading: '3. What this does not do',
      body: [
        'Deleting your account does not cancel an App Store or Google Play subscription. If you subscribed through a mobile app store, cancel the subscription separately in that store to stop future billing.',
        'We may retain a limited amount of information where we are required to keep it by law, for tax and accounting records, or to resolve disputes and enforce our agreements, as described in our Privacy Policy. Retained information is kept only as long as needed for those purposes.',
      ],
    },
    {
      heading: '4. Request deletion without the app',
      body: [
        `If you no longer have the app installed and cannot sign in, you can request deletion of your account and data by contacting ${M.entity} through ${M.contactDisplay}. Send your request from the email address associated with your account so we can verify your identity, and we will process the deletion.`,
      ],
    },
    {
      heading: '5. Contact',
      body: [
        `For any question about deleting your account or your data, contact ${M.entity} through ${M.contactDisplay}.`,
      ],
    },
  ],
};

/** Registry consumed by the Legal Center hub and the individual policy pages. */
export const LEGAL_DOCS: LegalDoc[] = [TERMS, PRIVACY, REFUNDS, DELETE_ACCOUNT];

export function legalDocBySlug(slug: LegalDoc['slug']): LegalDoc {
  const doc = LEGAL_DOCS.find((d) => d.slug === slug);
  if (!doc) throw new Error(`Unknown legal doc slug: ${slug}`);
  return doc;
}
